// Client-side learned aesthetic score (pipeline Stage 4) — main-thread side.
//
//   aligned canvas (facePrep) ─▶ downscale 224 ─▶ RGBA ImageData
//        ─▶ [transfer to Web Worker] ─▶ fromPixels/255 → predict → raw
//        ─▶ [back on main thread] toPercentile ─▶ { raw, percentile }
//
// ALL TF.js work runs in public/aesthetic.worker.js — model load, the ~3s
// first-inference WebGL shader compile, and every per-scan inference — so the
// scan UI never freezes. This module only orchestrates the worker, transfers
// pixels, and applies calibration (cheap, pure JS) on the result.
//
// EVERY failure path resolves to null so the Oracle verdict still renders
// Gemini-only — the learned score is supplementary, never a gate. Runs only on
// a facePrep-normalized crop (prep.normalized === true): the model's accuracy
// depends on the exact training-time alignment.
import {
  WORKER_PATH,
  MODEL_LOAD_TIMEOUT_MS,
  SCORE_TIMEOUT_MS,
} from './constants';
import { canvasToInputImageData } from './preprocess';
import { loadCalibration } from './calibration';

let worker = null;
let readyPromise = null;
let nextId = 1;
const pending = new Map(); // id -> resolve(raw|null)

function settle(id, value) {
  const entry = pending.get(id);
  if (entry) {
    pending.delete(id);
    entry(value);
  }
}

function createWorker() {
  if (typeof Worker === 'undefined') return null;
  let w;
  try {
    w = new Worker(WORKER_PATH);
  } catch (_e) {
    return null;
  }
  w.addEventListener('message', (e) => {
    const msg = (e && e.data) || {};
    if (msg.type === 'result' && msg.id != null) {
      settle(msg.id, Number.isFinite(msg.raw) ? msg.raw : null);
    } else if (msg.type === 'error' && msg.id != null) {
      settle(msg.id, null);
    }
  });
  w.addEventListener('error', () => {
    // A worker-level crash fails all in-flight scores; the scan degrades to
    // no-percentile rather than hanging.
    pending.forEach((resolve) => resolve(null));
    pending.clear();
  });
  return w;
}

// Kick (or join) worker creation + model load + in-worker warm-up. Resolves
// true once the worker reports ready, false on timeout/failure/unsupported.
export function warmAestheticModel() {
  if (readyPromise) return readyPromise;
  worker = createWorker();
  if (!worker) {
    readyPromise = Promise.resolve(false);
    return readyPromise;
  }
  // Warm the calibration table (main thread) alongside the worker's model load.
  try {
    loadCalibration();
  } catch (_e) {
    /* best-effort */
  }
  readyPromise = new Promise((resolve) => {
    const timer = setTimeout(() => {
      worker.removeEventListener('message', onReady);
      resolve(false);
    }, MODEL_LOAD_TIMEOUT_MS);
    function onReady(e) {
      const msg = (e && e.data) || {};
      if (msg.type === 'ready') {
        clearTimeout(timer);
        worker.removeEventListener('message', onReady);
        resolve(true);
      } else if (msg.type === 'error' && msg.id == null) {
        clearTimeout(timer);
        worker.removeEventListener('message', onReady);
        // Load failed — drop the worker so a later scan retries from scratch.
        worker = null;
        readyPromise = null;
        resolve(false);
      }
    }
    worker.addEventListener('message', onReady);
    worker.postMessage({ type: 'init' });
  });
  return readyPromise;
}

// Backward-compatible alias.
export function loadAestheticModel() {
  return warmAestheticModel();
}

// Score an aligned crop. Returns { raw, percentile } or null on any failure
// (worker unsupported/unready, no DOM canvas, inference error, timeout).
export async function scoreAligned(alignedCanvas) {
  if (!alignedCanvas) return null;
  let ready;
  try {
    ready = await warmAestheticModel();
  } catch (_e) {
    ready = false;
  }
  if (!ready || !worker) return null;

  let input;
  try {
    input = canvasToInputImageData(alignedCanvas);
  } catch (_e) {
    return null;
  }

  const id = nextId;
  nextId += 1;
  const raw = await new Promise((resolve) => {
    const timer = setTimeout(() => settle(id, null), SCORE_TIMEOUT_MS);
    pending.set(id, (value) => {
      clearTimeout(timer);
      resolve(value);
    });
    try {
      worker.postMessage(
        { type: 'score', id, width: input.width, height: input.height, buffer: input.buffer },
        [input.buffer], // transferable — zero-copy
      );
    } catch (_e) {
      settle(id, null);
    }
  });

  if (raw == null || !Number.isFinite(raw)) return null;
  const toPercentile = await loadCalibration();
  const percentile = toPercentile(raw);
  return { raw, percentile: Number.isFinite(percentile) ? percentile : null };
}

// Test-only: reset module state between cases.
export function _resetForTests() {
  if (worker && typeof worker.terminate === 'function') {
    try {
      worker.terminate();
    } catch (_e) {
      /* ignore */
    }
  }
  worker = null;
  readyPromise = null;
  pending.clear();
  nextId = 1;
}
